import { ChevronDown, FileText } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { usePainEntry } from "@/hooks/usePainEntries";
import { localDateOf } from "@/lib/body-measurements";
import { addDays, DAY_KEYS } from "@/lib/days";
import {
  PAIN_LEVELS,
  painLevelAnchor,
  SYMPTOM_CATALOG,
  type Symptom,
} from "@/lib/pain";
import { updatePainEntry } from "@/lib/repos/pain";
import type { PainLogConfig, PainLogDayData } from "@/lib/schema";
import { FutureDayCard, isFutureDate } from "@/widgets/FutureDayCard";
import type { WidgetDayViewProps } from "@/widgets/types";
import { useAutoCollapse } from "@/widgets/useAutoCollapse";
import { scoreDay } from "./scoring";

type Props = WidgetDayViewProps<PainLogConfig>;

export function DayView(props: Props) {
  // Pain entries are date-keyed and global — the viewed day maps to a date
  // via the week's startDate; the settings preview falls back to today.
  const date = props.week
    ? addDays(props.week.startDate, DAY_KEYS.indexOf(props.dayKey))
    : localDateOf(new Date());

  // Keyed by date so the note draft never leaks into another day when
  // navigating within a mounted week.
  if (!props.readOnly && isFutureDate(date)) {
    return (
      <FutureDayCard
        configId={props.config.id}
        label={props.config.label}
        kindClass="pain-log"
      />
    );
  }

  return <DateView key={date} date={date} {...props} />;
}

function DateView({
  config,
  data,
  date,
  dayKey,
  onChangeDay,
  readOnly = false,
}: Props & { date: string }) {
  const { entry, loading } = usePainEntry(date);
  const [noteDraft, setNoteDraft] = useState<string | null>(null);

  const day = (data ?? {}) as PainLogDayData;
  const level = entry?.level;
  const hasLevel = typeof level === "number";
  const finished = day.finished === true;
  const score = scoreDay(config, data);

  // Repair the week row's mirror when the shown day disagrees with its entry —
  // a level set while the week had no instance, or a week recreated after
  // «Slett uken» (docs/adr/0028). Runs only when the entry changes: a level
  // tap writes the entry first and the mirror after, and re-checking on the
  // mirror's own change would race that pair.
  const latest = useRef({ day, onChangeDay });
  latest.current = { day, onChangeDay };
  useEffect(() => {
    if (readOnly || loading) return;
    const current = latest.current.day;
    if (hasLevel === (current.level === true)) return;
    latest.current.onChangeDay({
      level: hasLevel,
      finished: hasLevel && current.finished === true,
    } satisfies PainLogDayData);
  }, [readOnly, loading, hasLevel]);

  const setLevel = async (next: number | null) => {
    if (readOnly) return;
    await updatePainEntry(date, { level: next });
    // Clearing the level takes «Ferdig for i dag» with it: a finished day
    // with no level claims more than was logged.
    onChangeDay({
      level: next !== null,
      finished: next !== null && finished,
    } satisfies PainLogDayData);
  };

  const toggleFinished = () => {
    if (readOnly || !hasLevel) return;
    onChangeDay({ ...day, finished: !finished } satisfies PainLogDayData);
  };

  const toggleSymptom = (symptom: Symptom) => {
    if (readOnly) return;
    const current = entry?.symptoms ?? [];
    const next = current.includes(symptom)
      ? current.filter((s) => s !== symptom)
      : [...current, symptom];
    void updatePainEntry(date, { symptoms: next });
  };

  const commitNote = () => {
    if (noteDraft === null) return;
    const draft = noteDraft;
    setNoteDraft(null);
    if (draft.trim() === (entry?.note ?? "")) return;
    void updatePainEntry(date, { note: draft });
  };

  // Ferdiglogget (CONTEXT.md): marked «Ferdig for i dag», which needs a
  // Smertenivå. A level alone is not enough — symptoms and the note come
  // after it, and greening the card at the level would call the day done
  // before they are in.
  const loggingCompleted = !readOnly && hasLevel && finished;
  const { open, toggle } = useAutoCollapse(dayKey, loggingCompleted, !loading);
  const collapsible = loggingCompleted;
  const collapsed = collapsible && !open;

  const symptomLabels = SYMPTOM_CATALOG.filter((s) =>
    entry?.symptoms.includes(s.key),
  ).map((s) => s.label);

  return (
    <section
      className={`widget-card pain-log${readOnly ? " read-only" : ""}${collapsed ? " collapsed" : ""}${loggingCompleted ? " logging-completed" : ""}`}
      data-testid={`widget-day-${config.id}`}
      data-logging-completed={loggingCompleted ? "true" : "false"}
    >
      <header className="widget-card-head">
        <h3 className="widget-card-title">{config.label}</h3>
        <div className="widget-card-head-right">
          <span
            className="widget-card-score"
            data-testid={`widget-score-${config.id}`}
          >
            <b>{score}</b>
            <span className="max"> / {config.points}p</span>
          </span>
          {collapsible ? (
            <button
              type="button"
              className="widget-card-toggle"
              onClick={toggle}
              aria-label={collapsed ? "Utvid" : "Skjul"}
              aria-expanded={!collapsed}
              data-testid={`widget-collapse-${config.id}`}
            >
              <ChevronDown className="chev" size={16} />
            </button>
          ) : null}
        </div>
      </header>
      {collapsed && hasLevel ? (
        <div
          className="widget-card-summary"
          data-testid={`pain-summary-${config.id}`}
        >
          <span className="summary-line">
            Smertenivå {level}
            {symptomLabels.length > 0 ? ` · ${symptomLabels.join(", ")}` : ""}
          </span>
        </div>
      ) : null}
      <div className="widget-card-body">
        {/* Every description is always on screen, so the number is picked
            against the words rather than against how yesterday felt. */}
        <fieldset className="pain-group">
          <legend className="pain-prompt hint">
            Hvor vondt har magen vært i dag?
          </legend>
          <div className="pain-levels">
            {PAIN_LEVELS.map((n) => (
              <button
                key={n}
                type="button"
                className={`pain-level${n === level ? " selected" : ""}`}
                disabled={readOnly}
                aria-pressed={n === level}
                onClick={() => void setLevel(n === level ? null : n)}
                data-testid={`pain-level-${n}-${config.id}`}
              >
                <span className="pain-level-number">{n}</span>
                <span className="pain-level-anchor">{painLevelAnchor(n)}</span>
              </button>
            ))}
          </div>
        </fieldset>
        <fieldset className="pain-group">
          <legend className="pain-section-title">Symptomer</legend>
          <div className="pain-symptoms">
            {SYMPTOM_CATALOG.map((symptom) => {
              const selected = entry?.symptoms.includes(symptom.key) ?? false;
              return (
                <button
                  key={symptom.key}
                  type="button"
                  className={selected ? "btn-primary" : "btn ghost"}
                  disabled={readOnly}
                  aria-pressed={selected}
                  onClick={() => toggleSymptom(symptom.key)}
                  data-testid={`pain-symptom-${symptom.key}-${config.id}`}
                >
                  {symptom.label}
                </button>
              );
            })}
          </div>
        </fieldset>
        <label
          className="pain-section-title"
          htmlFor={`pain-note-${config.id}`}
        >
          Notat
        </label>
        {/* Saved on blur. Enter is a line break here, not a commit. */}
        <textarea
          id={`pain-note-${config.id}`}
          className="input pain-note"
          rows={5}
          placeholder="Valgfritt – f.eks. når det var vondt, eller hva du tror kom av det"
          disabled={readOnly}
          value={noteDraft ?? entry?.note ?? ""}
          onChange={(e) => setNoteDraft(e.target.value)}
          onBlur={commitNote}
          data-testid={`pain-note-input-${config.id}`}
        />
        {!readOnly ? (
          <div className="pain-finish">
            <Link
              to="/rapport"
              className="btn ghost icon-btn"
              data-testid={`report-button-${config.id}`}
            >
              <FileText size={14} />
              Rapport
            </Link>
            {/* Not offered before a level is set: there is nothing to be done
                with yet. */}
            {hasLevel ? (
              <button
                type="button"
                className={finished ? "btn ghost" : "btn-primary"}
                onClick={toggleFinished}
                data-testid={`pain-finish-button-${config.id}`}
              >
                {finished ? "Åpne dagen igjen" : "Ferdig for i dag"}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
